import React, { useState } from 'react';
import {
    HelixLoader,
    MultilineTextField,
    PageWrapper,
    UpdatePageHeader,
} from 'components';
import { useNavigate, useParams } from 'react-router-dom';
import { yupResolver } from '@hookform/resolvers/yup';
import { Controller, useForm } from 'react-hook-form';
import {
    createOrUpdateSmartContractDefaults,
    CreateOrUpdateSmartContractFormData,
    CreateOrUpdateSmartContractSchema,
    createOrUpdateSmartContractStyles,
} from './create-or-update-smart-contract-helper';
import { SMART_CONTRACT_PATHS } from 'constants/smart-contracts-paths';
import { useDeleteSmartContract, useSdo, useUpdateSmartContract } from 'hooks';
import { Box, Grid, Typography } from '@mui/material';
import { ErrorMessage, Fab, FormLabel, TextField } from '@biq/apex-ui';
import CodeMirror from '@uiw/react-codemirror';
import { CodeMirrorStyles } from 'lib';
import { javascript } from '@codemirror/lang-javascript';
import { useAuth } from '../../../core';
import { Icon } from '@iconify/react';

export const UpdateSmartContract = (): React.JSX.Element => {
    const { getToken } = useAuth();
    const { sdoId, chain } = useParams();
    const navigate = useNavigate();
    const { mutate: deleteSmartContract } = useDeleteSmartContract();
    const { mutate: updateSmartContract } = useUpdateSmartContract();
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [isChangesUnsaved, setIsChangesUnsaved] = useState<boolean>(false);
    const [openCancelModal, setOpenCancelModal] = useState<boolean>(false);
    const [openExecuteModal, setOpenExecuteModal] = useState<boolean>(false);
    const [deserializedCode, setDeserializedCode] = useState<
        string | undefined
    >();
    const {
        handleSubmit,
        control,
        setValue,
        clearErrors,
        getValues,
        formState: { errors },
    } = useForm<CreateOrUpdateSmartContractFormData>({
        mode: 'all',
        resolver: yupResolver(CreateOrUpdateSmartContractSchema),
        defaultValues: createOrUpdateSmartContractDefaults,
    });

    const onSuccess = (data: any): void => {
        setValue('name', data?.name ?? '');
        setValue('description', data?.description ?? '');
        if (data.codeBase64) {
            const code = atob(data.codeBase64) || '';
            setValue('javaScript', code);
            setDeserializedCode(code);
        }
    };
    const onError = (): void => {};

    const { isLoading: smartContractDataLoading } = useSdo({
        chain: chain,
        sdoId: sdoId,
        onSuccess,
        onError,
    });

    const onSaveButtonClick = (
        data: CreateOrUpdateSmartContractFormData
    ): void => {
        setIsLoading(true);
        updateSmartContract(
            {
                sdoId: sdoId as string,
                smartContract: {
                    name: data.name,
                    description: data.description,
                    javaScript: deserializedCode as string,
                },
                chain: chain as string,
                token: getToken(),
            },
            {
                onSuccess: () => {
                    setIsLoading(false);
                    navigate(SMART_CONTRACT_PATHS.LANDING_PAGE, {
                        state: chain,
                    });
                },
                onError: () => {
                    setIsLoading(false);
                },
            }
        );
    };

    const handleCancelButton = (): void => {
        if (isChangesUnsaved) {
            setOpenCancelModal(true);
        } else {
            navigate(SMART_CONTRACT_PATHS.LANDING_PAGE, {
                state: chain,
            });
        }
    };

    const handleCancelModalConfirmation = (): void => {
        setOpenCancelModal(false);
        navigate(SMART_CONTRACT_PATHS.LANDING_PAGE, {
            state: chain,
        });
    };

    const handleCancelModal = (): void => {
        setOpenCancelModal(false);
    };

    const handleConfirmation = (): void => {
        setTimeout(() => {
            navigate(SMART_CONTRACT_PATHS.LANDING_PAGE, {
                state: chain,
            });
        }, 500);
        deleteSmartContract({
            chainID: sdoId as string,
            chainName: chain as string,
        });
    };

    const handleCodeChange = React.useCallback((value: string) => {
        setIsChangesUnsaved(true);
        setDeserializedCode(value);
        setValue('javaScript', value);
        clearErrors('javaScript');
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const handleExecuteSmartContract = (): void => {
        setIsLoading(true);
        updateSmartContract(
            {
                sdoId: sdoId as string,
                smartContract: {
                    name: getValues().name,
                    description: getValues().description,
                    javaScript: deserializedCode as string,
                },
                chain: chain as string,
                token: getToken(),
            },
            {
                onSuccess: () => {
                    setIsLoading(false);
                    navigate(
                        `${SMART_CONTRACT_PATHS.EXECUTE_SMART_CONTRACT}?chain=${chain}&sdoId=${sdoId}`
                    );
                },
                onError: () => {
                    setIsLoading(false);
                },
            }
        );
    };

    const handleExecuteModal = (): void => {
        if (isChangesUnsaved) {
            setOpenExecuteModal(true);
        } else {
            handleExecuteSmartContract();
        }
    };

    const handleCloseExecuteModal = (): void => {
        setOpenExecuteModal(false);
    };

    if (smartContractDataLoading) {
        return <HelixLoader />;
    }

    return (
        <PageWrapper
            title={'Edit Smart Contract'}
            includeBreadcrumbs={true}
            breadCrumStatus='update'
            breadCrumType='Smart_Contract'
            breadCrumData={chain}
        >
            <UpdatePageHeader
                headingText={'General Info'}
                deleteMenuItemText={'Delete Contract'}
                handleSaveButton={handleSubmit(onSaveButtonClick)}
                handleCancelButton={handleCancelButton}
                isExecuteContractMenuVisible={true}
                isLoading={isLoading}
                isChangesUnsaved={isChangesUnsaved}
                openCancelModal={openCancelModal}
                handleCancelModalConfirmation={handleCancelModalConfirmation}
                handleCancelModal={handleCancelModal}
                handleExecuteContract={handleExecuteModal}
                unsavedChangesConfirmationText={
                    'This smart contract has unsaved changes. Are you sure you want to close without saving? You will lose all changes.'
                }
                openExecuteModal={openExecuteModal}
                handleCloseExecuteModal={handleCloseExecuteModal}
                handleExecuteSmartContract={handleExecuteSmartContract}
                confirmationModalConfig={{
                    confirmationButtonText: 'Yes, Delete',
                    confirmationHeadingText: 'Confirm Smart Contract Deletion',
                    handleConfirmation: handleConfirmation,
                    confirmationCancelButtonText: 'No, Cancel',
                    hasConfirmationTextField: false,
                    confirmationSubHeadingText: (
                        <>
                            Are you sure you would like to delete your smart
                            contract?
                        </>
                    ),
                }}
                menuWidth={'251px'}
            />
            <Grid sx={createOrUpdateSmartContractStyles.updateContainerStyles}>
                <Grid
                    sx={
                        createOrUpdateSmartContractStyles.textFieldContainerStyles
                    }
                >
                    <Controller
                        name='name'
                        control={control}
                        render={({ field }): React.JSX.Element => (
                            <TextField
                                {...field}
                                variant='outlined'
                                label='Name'
                                required
                                onChange={(value): void => {
                                    setIsChangesUnsaved(true);
                                    field.onChange(value);
                                }}
                                error={!!errors.name}
                                errorText={
                                    errors.name ? errors.name.message : ''
                                }
                            />
                        )}
                    />
                </Grid>
                <Grid
                    sx={createOrUpdateSmartContractStyles.gridContainerStyles}
                >
                    <MultilineTextField
                        control={control}
                        label='Description'
                        name='description'
                        error={!!errors.description}
                        errorText={
                            errors.description ? errors.description.message : ''
                        }
                        onChange={(): void => {
                            setIsChangesUnsaved(true);
                        }}
                    />
                </Grid>
            </Grid>
            <Grid
                sx={
                    createOrUpdateSmartContractStyles.smartContractTextContainerStyles
                }
            >
                <Typography
                    sx={
                        createOrUpdateSmartContractStyles.smartContractTextStyles
                    }
                >
                    Smart Contract
                </Typography>
            </Grid>
            <Grid
                sx={
                    createOrUpdateSmartContractStyles.smartContractFieldContainerStyles
                }
            >
                <Grid>
                    <Controller
                        name='javaScript'
                        control={control}
                        render={(): React.JSX.Element => (
                            <>
                                <Grid
                                    sx={
                                        createOrUpdateSmartContractStyles.labelAndButtonContainerStyles
                                    }
                                >
                                    <Grid
                                        sx={
                                            createOrUpdateSmartContractStyles.labelContainerStyles
                                        }
                                    >
                                        <FormLabel
                                            required
                                            label='Smart Contract'
                                        />
                                    </Grid>
                                    <Fab
                                        type='button'
                                        sx={
                                            createOrUpdateSmartContractStyles.fabButtonStyles
                                        }
                                        icon={
                                            <Icon
                                                icon='mdi:file-sign'
                                                style={
                                                    createOrUpdateSmartContractStyles.fabButtonIconStyles
                                                }
                                            />
                                        }
                                        color='secondary'
                                        label='Execute Contract'
                                        onClick={handleExecuteModal}
                                    />
                                </Grid>
                                <CodeMirror
                                    value={deserializedCode}
                                    style={{
                                        ...(errors?.javaScript
                                            ? createOrUpdateSmartContractStyles.javaScriptError
                                            : createOrUpdateSmartContractStyles.javaScript),
                                    }}
                                    height={
                                        createOrUpdateSmartContractStyles.codeMirrorHeight
                                    }
                                    extensions={[
                                        javascript({ jsx: true }),
                                        CodeMirrorStyles,
                                    ]}
                                    onChange={handleCodeChange}
                                />
                                {errors?.javaScript && (
                                    <Box
                                        style={
                                            createOrUpdateSmartContractStyles.javaScriptErrorContainer
                                        }
                                    >
                                        <ErrorMessage
                                            errorMessage={
                                                errors?.javaScript.message ?? ''
                                            }
                                        />
                                    </Box>
                                )}
                            </>
                        )}
                    />
                </Grid>
            </Grid>
        </PageWrapper>
    );
};
